import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Modal_proyectos from '../../menus/Modal';
import { add_internal_award, get_strategy_activity_map, list_all_users_task } from '../../../utils/talent_services';
import UserAvatar from './UserAvatar';
import Statistics from '../talento/Statistics';
import Task_progress from '../talento/Task_progress';
import 'bootstrap-icons/font/bootstrap-icons.css';

const PERIOD_OPTIONS = [
    { value: 7, label: '7 dias' },
    { value: 15, label: '15 dias' },
    { value: 30, label: '30 dias' },
    { value: 60, label: '60 dias' }
];

const CARD_W = 130;
const CARD_H = 68;
const MIN_GRID_GAP_X = 24;
const GRID_GAP_Y = 24;
const AVATAR_SIZE = 44;
const KR_VENTAS_TRELLO_ID = '6668700517c510bb035f5603';
const KR_OPERACIONES_TRELLO_ID = '666b6ae1fb8e47b8bec18c6c';
const KR_PRODUCTO_TRELLO_ID = '67a0dac463d59f37ebe2e664';

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function truncateTitle(text, max = 20) {
    const safe = String(text || '').trim();
    if (safe.length <= max) return safe;
    return `${safe.slice(0, max)}...`;
}

function resolveKrBucket(strategy) {
    if (strategy?.is_unassigned || strategy?.strategy_key === 'unassigned') return 'unassigned';
    if (strategy?.is_opportunity) return 'ventas';
    const krId = String(strategy?.kr_trello_id || '').trim().toLowerCase();
    const krName = String(strategy?.kr_name || '').trim().toLowerCase();
    if (krId === KR_VENTAS_TRELLO_ID || krName === 'ventas') return 'ventas';
    if (krId === KR_PRODUCTO_TRELLO_ID || krName === 'producto') return 'producto';
    if (krId === KR_OPERACIONES_TRELLO_ID || krName === 'operaciones') return 'operaciones';
    return 'operaciones';
}

function getZoneColumnCount(contentWidthInput, strategyCount) {
    const count = Math.max(0, Number(strategyCount) || 0);
    if (count <= 1) return 1;
    const contentWidth = Math.max(1, Number(contentWidthInput) || 0);
    const minGap = 6;
    const maxThatFit = Math.floor((contentWidth + minGap) / (CARD_W + minGap));
    return Math.max(1, Math.min(count, maxThatFit));
}

function Current_activity_map({ token }) {
    const [periodDays, setPeriodDays] = useState(15);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [error, setError] = useState('');
    const [payload, setPayload] = useState({
        strategies: [],
        users: [],
        user_strategy_activity: [],
        user_totals: []
    });
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedStrategyKey, setSelectedStrategyKey] = useState(null);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [selectedTaskStats, setSelectedTaskStats] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [selectedFeedbackUser, setSelectedFeedbackUser] = useState(null);
    const [feedbackComment, setFeedbackComment] = useState('');
    const [feedbackSending, setFeedbackSending] = useState(false);
    const [showUserStatsModal, setShowUserStatsModal] = useState(false);
    const [selectedUserStats, setSelectedUserStats] = useState(null);
    const isInitialLoad = useRef(true);
    const canvasViewportRef = useRef(null);
    const [canvasViewportWidth, setCanvasViewportWidth] = useState(0);

    useEffect(() => {
        const node = canvasViewportRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;

        const updateWidth = () => {
            setCanvasViewportWidth(node.clientWidth || 0);
        };

        updateWidth();
        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(node);

        return () => observer.disconnect();
    }, [loading]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        const loadData = async (showLoader = false) => {
            try {
                if (showLoader) {
                    setLoading(true);
                } else {
                    setIsRefreshing(true);
                }
                setError('');
                const data = await get_strategy_activity_map(token, periodDays);
                if (cancelled) return;

                if (data === null || data === 'INVALID') {
                    setError('No se pudo cargar el mapa de actividad.');
                    setPayload({
                        strategies: [],
                        users: [],
                        user_strategy_activity: [],
                        user_totals: []
                    });
                    setLoading(false);
                    return;
                }

                setPayload({
                    strategies: Array.isArray(data.strategies) ? data.strategies : [],
                    users: Array.isArray(data.users) ? data.users : [],
                    user_strategy_activity: Array.isArray(data.user_strategy_activity) ? data.user_strategy_activity : [],
                    user_totals: Array.isArray(data.user_totals) ? data.user_totals : []
                });
                setLoading(false);
            } finally {
                if (!cancelled) {
                    setIsRefreshing(false);
                }
            }
        };

        loadData(isInitialLoad.current);
        isInitialLoad.current = false;
        const interval = setInterval(() => loadData(false), 30000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [token, periodDays, refreshNonce]);

    const strategies = payload.strategies || [];
    const strategiesSorted = useMemo(() => {
        const cloned = [...strategies];
        cloned.sort((a, b) => {
            const aUsers = Number(a?.totals?.user_count) || 0;
            const bUsers = Number(b?.totals?.user_count) || 0;
            if (aUsers !== bUsers) return bUsers - aUsers;
            const aMinutes = Number(a?.totals?.total_minutes) || 0;
            const bMinutes = Number(b?.totals?.total_minutes) || 0;
            if (aMinutes !== bMinutes) return bMinutes - aMinutes;
            return String(a?.strategy_name || '').localeCompare(String(b?.strategy_name || ''));
        });
        return cloned;
    }, [strategies]);

    const users = payload.users || [];
    const activity = payload.user_strategy_activity || [];
    const userTotals = payload.user_totals || [];

    const userTotalsMap = useMemo(() => {
        const map = {};
        userTotals.forEach((row) => {
            map[Number(row.usr_id)] = {
                total_minutes: Number(row.total_minutes) || 0,
                has_any_active_task: Boolean(row.has_any_active_task)
            };
        });
        return map;
    }, [userTotals]);

    const userLinksMap = useMemo(() => {
        const map = {};
        activity.forEach((link) => {
            const uid = Number(link.usr_id);
            if (!map[uid]) map[uid] = [];
            map[uid].push({
                strategy_key: link.strategy_key,
                minutes: Number(link.minutes) || 0,
                has_active_task: Boolean(link.has_active_task),
                active_task_count: Number(link.active_task_count) || 0,
                active_task_name: String(link.active_task_name || '').trim()
            });
        });
        return map;
    }, [activity]);

    const strategyByKey = useMemo(() => {
        const map = {};
        strategiesSorted.forEach((strategy) => {
            map[strategy.strategy_key] = strategy;
        });
        return map;
    }, [strategiesSorted]);

    const usersSorted = useMemo(() => {
        const cloned = [...users];
        cloned.sort((a, b) => {
            const aActive = userTotalsMap[Number(a.usr_id)]?.has_any_active_task ? 1 : 0;
            const bActive = userTotalsMap[Number(b.usr_id)]?.has_any_active_task ? 1 : 0;
            if (aActive !== bActive) return bActive - aActive;
            return String(a.usr_name || '').localeCompare(String(b.usr_name || ''));
        });
        return cloned;
    }, [users, userTotalsMap]);

    const usersWithPeriodTasks = useMemo(
        () => usersSorted.filter((u) => (userTotalsMap[Number(u.usr_id)]?.total_minutes || 0) > 0),
        [usersSorted, userTotalsMap]
    );

    const usersWithoutPeriodTasks = useMemo(
        () => usersSorted.filter((u) => (userTotalsMap[Number(u.usr_id)]?.total_minutes || 0) <= 0),
        [usersSorted, userTotalsMap]
    );
    const inactiveNowUsers = useMemo(
        () => usersWithPeriodTasks.filter((u) => !Boolean(userTotalsMap[Number(u.usr_id)]?.has_any_active_task)),
        [usersWithPeriodTasks, userTotalsMap]
    );

    const strategyUserIdsMap = useMemo(() => {
        const map = {};
        activity.forEach((link) => {
            const key = link.strategy_key;
            if (!map[key]) map[key] = new Set();
            map[key].add(Number(link.usr_id));
        });
        return map;
    }, [activity]);

    const visibleStrategyKeys = useMemo(() => {
        if (selectedUserId == null && selectedStrategyKey == null) return null;

        const keys = new Set();
        if (selectedUserId != null) {
            const links = userLinksMap[Number(selectedUserId)] || [];
            links.forEach((link) => keys.add(link.strategy_key));
        }
        if (selectedStrategyKey != null) {
            if (selectedUserId == null) return new Set([selectedStrategyKey]);
            if (keys.has(selectedStrategyKey)) return new Set([selectedStrategyKey]);
            return new Set();
        }
        return keys;
    }, [selectedUserId, selectedStrategyKey, userLinksMap]);

    const displayedStrategies = useMemo(() => {
        if (visibleStrategyKeys == null) return strategiesSorted;
        return strategiesSorted.filter((strategy) => visibleStrategyKeys.has(strategy.strategy_key));
    }, [strategiesSorted, visibleStrategyKeys]);

    const displayedStrategyKeySet = useMemo(
        () => new Set(displayedStrategies.map((s) => s.strategy_key)),
        [displayedStrategies]
    );

    const isFocusMode = selectedUserId != null || selectedStrategyKey != null;

    const visibleCanvasUsers = useMemo(() => {
        let result = usersWithPeriodTasks;
        if (selectedUserId != null) {
            result = result.filter((u) => Number(u.usr_id) === Number(selectedUserId));
        }
        if (selectedStrategyKey != null) {
            const allowedUsers = strategyUserIdsMap[selectedStrategyKey] || new Set();
            result = result.filter((u) => allowedUsers.has(Number(u.usr_id)));
        }
        return result;
    }, [usersWithPeriodTasks, selectedUserId, selectedStrategyKey, strategyUserIdsMap]);

    const strategiesByKr = useMemo(() => {
        const groups = { ventas: [], producto: [], operaciones: [] };
        strategiesSorted.forEach((strategy) => {
            const bucket = resolveKrBucket(strategy);
            if (bucket === 'unassigned') return;
            groups[bucket].push(strategy);
        });
        return groups;
    }, [strategiesSorted]);
    const unassignedStrategies = useMemo(
        () => strategiesSorted.filter((strategy) => resolveKrBucket(strategy) === 'unassigned'),
        [strategiesSorted]
    );

    const usersAreaHeight = 95 + Math.ceil(usersWithPeriodTasks.length / 3) * 80;
    const canvasWidth = Math.max(320, canvasViewportWidth);

    const krZones = useMemo(() => {
        const outerPadding = 10;
        const zoneGap = 8;
        const bottomGap = 8;
        const sectionTitleHeight = 24;
        const innerPadding = 10;
        const minZoneHeight = 150;

        const fullWidth = canvasWidth - outerPadding * 2;
        const bottomZoneWidth = (fullWidth - zoneGap) / 2;
        const topZoneWidth = fullWidth;

        const estimateZoneHeight = (count, zoneWidth) => {
            const contentWidth = Math.max(1, zoneWidth - innerPadding * 2);
            const cols = getZoneColumnCount(contentWidth, count);
            const rows = Math.max(1, Math.ceil((count || 1) / cols));
            const cardsHeight = rows * CARD_H + Math.max(0, rows - 1) * GRID_GAP_Y;
            return Math.max(minZoneHeight, sectionTitleHeight + innerPadding * 2 + cardsHeight + 8);
        };

        const topHeight = estimateZoneHeight(strategiesByKr.ventas.length, topZoneWidth);
        const leftHeight = estimateZoneHeight(strategiesByKr.producto.length, bottomZoneWidth);
        const rightHeight = estimateZoneHeight(strategiesByKr.operaciones.length, bottomZoneWidth);
        const bottomHeight = Math.max(leftHeight, rightHeight);

        const totalHeight = outerPadding + topHeight + bottomGap + bottomHeight + outerPadding;

        return {
            totalHeight,
            ventas: {
                title: 'Ventas',
                x: outerPadding,
                y: outerPadding,
                w: topZoneWidth,
                h: topHeight,
                contentX: outerPadding + innerPadding,
                contentY: outerPadding + sectionTitleHeight + innerPadding,
                contentW: topZoneWidth - innerPadding * 2
            },
            producto: {
                title: 'Producto',
                x: outerPadding,
                y: outerPadding + topHeight + bottomGap,
                w: bottomZoneWidth,
                h: bottomHeight,
                contentX: outerPadding + innerPadding,
                contentY: outerPadding + topHeight + bottomGap + sectionTitleHeight + innerPadding,
                contentW: bottomZoneWidth - innerPadding * 2
            },
            operaciones: {
                title: 'Operaciones',
                x: outerPadding + bottomZoneWidth + zoneGap,
                y: outerPadding + topHeight + bottomGap,
                w: bottomZoneWidth,
                h: bottomHeight,
                contentX: outerPadding + bottomZoneWidth + zoneGap + innerPadding,
                contentY: outerPadding + topHeight + bottomGap + sectionTitleHeight + innerPadding,
                contentW: bottomZoneWidth - innerPadding * 2
            }
        };
    }, [canvasWidth, strategiesByKr]);

    const canvasHeight = Math.max(700, krZones.totalHeight, usersAreaHeight + 200);

    const strategyPositions = useMemo(() => {
        const map = {};
        const placeInZone = (strategies, zone) => {
            if (!zone || strategies.length === 0) return;
            const cols = getZoneColumnCount(zone.contentW, strategies.length);
            const rows = Math.max(1, Math.ceil(strategies.length / cols));
            const rowGap = GRID_GAP_Y;
            const cardsBlockWidth = cols * CARD_W;
            const cardsBlockHeight = rows * CARD_H + Math.max(0, rows - 1) * rowGap;
            const colGap = cols > 1 ? Math.max(6, (zone.contentW - cardsBlockWidth) / (cols - 1)) : 0;
            const startY = zone.contentY + Math.max(0, (zone.h - (zone.contentY - zone.y) - cardsBlockHeight - 12) / 2);

            strategies.forEach((strategy, index) => {
                const row = Math.floor(index / cols);
                const col = index % cols;
                const x = zone.contentX + col * (CARD_W + colGap);
                const y = startY + row * (CARD_H + rowGap);
                map[strategy.strategy_key] = {
                    x,
                    y,
                    cx: x + CARD_W / 2,
                    cy: y + CARD_H / 2
                };
            });
        };

        placeInZone(strategiesByKr.ventas, krZones.ventas);
        placeInZone(strategiesByKr.producto, krZones.producto);
        placeInZone(strategiesByKr.operaciones, krZones.operaciones);

        if (unassignedStrategies.length > 0 && krZones.operaciones) {
            const zone = krZones.operaciones;
            const allOps = [...strategiesByKr.operaciones, ...unassignedStrategies];
            const cols = getZoneColumnCount(zone.contentW, allOps.length);
            const rowGap = GRID_GAP_Y;
            const cardsBlockWidth = cols * CARD_W;
            const colGap = cols > 1 ? Math.max(6, (zone.contentW - cardsBlockWidth) / (cols - 1)) : 0;

            unassignedStrategies.forEach((strategy, index) => {
                const globalIndex = strategiesByKr.operaciones.length + index;
                const row = Math.floor(globalIndex / cols);
                const col = globalIndex % cols;
                const x = zone.contentX + col * (CARD_W + colGap);
                const y = zone.contentY + row * (CARD_H + rowGap);
                map[strategy.strategy_key] = {
                    x,
                    y,
                    cx: x + CARD_W / 2,
                    cy: y + CARD_H / 2
                };
            });
        }
        return map;
    }, [strategiesByKr, krZones, unassignedStrategies]);

    const userQuadrantMeta = useMemo(() => {
        const meta = {};
        usersWithPeriodTasks.forEach((user) => {
            const uid = Number(user.usr_id);
            const links = userLinksMap[uid] || [];
            if (links.length === 0) {
                meta[uid] = {
                    isSingleQuadrant: false,
                    dominantBucket: null
                };
                return;
            }

            const bucketsWithLinks = new Set();
            const minutesByBucket = { ventas: 0, producto: 0, operaciones: 0 };
            links.forEach((link) => {
                const strategy = strategyByKey[link.strategy_key];
                if (!strategy) return;
                const bucket = resolveKrBucket(strategy);
                bucketsWithLinks.add(bucket);
                minutesByBucket[bucket] += Math.max(0, Number(link.minutes) || 0);
            });

            const sortedBuckets = Object.entries(minutesByBucket).sort((a, b) => b[1] - a[1]);
            meta[uid] = {
                isSingleQuadrant: bucketsWithLinks.size === 1,
                dominantBucket: sortedBuckets[0]?.[0] || null
            };
        });
        return meta;
    }, [usersWithPeriodTasks, userLinksMap, strategyByKey]);

    const userPositions = useMemo(() => {
        const positions = {};
        const zonePad = AVATAR_SIZE / 2 + 6;
        const clampPointToZone = (uid, point) => {
            const userMeta = userQuadrantMeta[uid];
            if (!userMeta?.isSingleQuadrant || !userMeta?.dominantBucket) return;
            const zone = krZones[userMeta.dominantBucket];
            if (!zone) return;
            const minX = zone.x + zonePad;
            const maxX = zone.x + zone.w - zonePad;
            const minY = zone.y + zonePad;
            const maxY = zone.y + zone.h - zonePad;
            // Sesgo suave a la zona superior derecha del cuadrante (menos densa).
            const targetX = zone.x + zone.w * 0.72;
            const targetY = zone.y + zone.h * 0.30;
            const pullToCorner = 0.1;
            point.x = point.x * (1 - pullToCorner) + targetX * pullToCorner;
            point.y = point.y * (1 - pullToCorner) + targetY * pullToCorner;
            point.x = clamp(point.x, minX, maxX);
            point.y = clamp(point.y, minY, maxY);
        };

        if (selectedStrategyKey != null && strategyPositions[selectedStrategyKey]) {
            const strategyCenter = strategyPositions[selectedStrategyKey];
            const strategyUsers = usersWithPeriodTasks
                .map((user) => {
                    const uid = Number(user.usr_id);
                    const link = (userLinksMap[uid] || []).find((item) => item.strategy_key === selectedStrategyKey);
                    return link ? { user, uid, minutes: Math.max(0, Number(link.minutes) || 0) } : null;
                })
                .filter(Boolean);

            if (strategyUsers.length > 0) {
                strategyUsers.sort((a, b) => b.minutes - a.minutes);
                const totalMinutes = Math.max(
                    1,
                    strategyUsers.reduce((acc, item) => acc + item.minutes, 0)
                );
                const angleStep = (Math.PI * 2) / Math.max(1, strategyUsers.length);
                const minRadius = 92;
                const maxRadius = 260;
                const startAngle = -Math.PI / 2;

                strategyUsers.forEach((item, index) => {
                    const share = clamp(item.minutes / totalMinutes, 0, 1);
                    // Mas horas => mas cerca de la estrategia.
                    const radius = maxRadius - share * (maxRadius - minRadius);
                    const angle = startAngle + index * angleStep;
                    positions[item.uid] = {
                        x: strategyCenter.cx + Math.cos(angle) * radius,
                        y: strategyCenter.cy + Math.sin(angle) * radius
                    };
                    clampPointToZone(item.uid, positions[item.uid]);
                });
            }
        }

        const linkedUsers = [];
        const unlinkedUsers = [];

        usersWithPeriodTasks.forEach((user) => {
            const uid = Number(user.usr_id);
            if (positions[uid]) return;
            const links = userLinksMap[uid] || [];
            if (links.length > 0) linkedUsers.push(user);
            else unlinkedUsers.push(user);
        });

        linkedUsers.forEach((user, idx) => {
            const uid = Number(user.usr_id);
            const links = userLinksMap[uid] || [];
            const totalMinutes = Math.max(
                1,
                links.reduce((acc, item) => acc + item.minutes, 0)
            );

            let weightedX = 0;
            let weightedY = 0;
            links.forEach((item) => {
                const pos = strategyPositions[item.strategy_key];
                if (!pos) return;
                const w = clamp(item.minutes / totalMinutes, 0, 1);
                weightedX += pos.cx * w;
                weightedY += pos.cy * w;
            });

            const baseX = 70 + (idx % 3) * 84;
            const baseY = 42 + Math.floor(idx / 3) * 80;
            const pull = 0.72;

            positions[uid] = {
                x: baseX * (1 - pull) + weightedX * pull,
                y: baseY * (1 - pull) + weightedY * pull
            };
            clampPointToZone(uid, positions[uid]);
        });

        unlinkedUsers.forEach((user, idx) => {
            const uid = Number(user.usr_id);
            positions[uid] = {
                x: 70 + (idx % 3) * 84,
                y: 50 + Math.floor(idx / 3) * 80
            };
        });

        const allUsers = usersWithPeriodTasks.map((u) => Number(u.usr_id));
        const strategyRects = strategiesSorted.map((strategy) => {
            const p = strategyPositions[strategy.strategy_key];
            if (!p) return null;
            return {
                left: p.x,
                right: p.x + CARD_W,
                top: p.y,
                bottom: p.y + CARD_H
            };
        }).filter(Boolean);

        const keepOutsideStrategies = (point) => {
            const padding = 26;
            strategyRects.forEach((rect) => {
                const insideX = point.x > rect.left - padding && point.x < rect.right + padding;
                const insideY = point.y > rect.top - padding && point.y < rect.bottom + padding;
                if (!insideX || !insideY) return;

                const distLeft = Math.abs(point.x - (rect.left - padding));
                const distRight = Math.abs((rect.right + padding) - point.x);
                const distTop = Math.abs(point.y - (rect.top - padding));
                const distBottom = Math.abs((rect.bottom + padding) - point.y);
                const minDist = Math.min(distLeft, distRight, distTop, distBottom);

                if (minDist === distLeft) point.x = rect.left - padding;
                else if (minDist === distRight) point.x = rect.right + padding;
                else if (minDist === distTop) point.y = rect.top - padding;
                else point.y = rect.bottom + padding;
            });
        };

        for (let iteration = 0; iteration < 30; iteration += 1) {
            for (let i = 0; i < allUsers.length; i += 1) {
                for (let j = i + 1; j < allUsers.length; j += 1) {
                    const idA = allUsers[i];
                    const idB = allUsers[j];
                    const a = positions[idA];
                    const b = positions[idB];
                    if (!a || !b) continue;
                    const dx = b.x - a.x;
                    const dy = b.y - a.y;
                    const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
                    const minDist = 58;
                    if (dist >= minDist) continue;
                    const push = (minDist - dist) * 0.5;
                    const ux = dx / dist;
                    const uy = dy / dist;
                    a.x -= ux * push;
                    a.y -= uy * push;
                    b.x += ux * push;
                    b.y += uy * push;
                }
            }

            allUsers.forEach((uid) => {
                const point = positions[uid];
                if (!point) return;
                keepOutsideStrategies(point);
                clampPointToZone(uid, point);
                point.x = clamp(point.x, 24, canvasWidth - 24);
                point.y = clamp(point.y, 24, canvasHeight - 24);
            });
        }

        return positions;
    }, [usersWithPeriodTasks, userLinksMap, strategyPositions, strategiesSorted, canvasWidth, canvasHeight, selectedStrategyKey, userQuadrantMeta, krZones]);

    const openActiveTaskStats = async (usrId, link, fallbackTitle) => {
        if (!token || !link?.has_active_task) return;
        const strategyKey = String(link.strategy_key || '');
        if (!strategyKey.startsWith('card:')) return;
        const cardDbId = Number(strategyKey.replace('card:', ''));
        if (!Number.isFinite(cardDbId) || cardDbId <= 0) return;

        const taskList = await list_all_users_task(token, null, periodDays, 'creation');
        if (!taskList || taskList === 'INVALID') return;
        const rows = Array.isArray(taskList.all_tasks) ? taskList.all_tasks : [];
        let activeTasks = rows.filter(
            (row) =>
                Number(row.card_id) === cardDbId &&
                Number(row.usr_id) === Number(usrId) &&
                Number(row.active) === 1 &&
                Number(row.completed) === 0
        );

        const activeTaskName = String(link.active_task_name || '').trim();
        if (activeTaskName) {
            const byName = activeTasks.filter(
                (row) => String(row.task_name || '').trim().toLowerCase() === activeTaskName.toLowerCase()
            );
            if (byName.length > 0) activeTasks = byName;
        }
        if (activeTasks.length === 0) return;

        activeTasks.sort((a, b) => {
            const aMinutes = Number(a.Tiempo_Activo_Total_Minutos_Enteros) || 0;
            const bMinutes = Number(b.Tiempo_Activo_Total_Minutos_Enteros) || 0;
            return bMinutes - aMinutes;
        });

        const pickedTask = activeTasks[0];
        const taskTitle = String(pickedTask.task_name || '').trim() || fallbackTitle || 'Tarea activa';
        setSelectedTaskStats({
            taskId: Number(pickedTask.id),
            usrId: Number(pickedTask.usr_id),
            title: taskTitle
        });
        setShowStatsModal(true);
    };

    const openFeedbackModal = (e, user) => {
        e.stopPropagation();
        setSelectedFeedbackUser({
            usr_id: Number(user.usr_id),
            usr_name: user.usr_name || user.email_id || 'Usuario'
        });
        setFeedbackComment('');
        setShowFeedbackModal(true);
    };

    const closeFeedbackModal = () => {
        setShowFeedbackModal(false);
        setSelectedFeedbackUser(null);
        setFeedbackComment('');
    };

    const handleSendFeedback = async () => {
        if (!selectedFeedbackUser || !token || !feedbackComment.trim()) return;
        setFeedbackSending(true);
        const result = await add_internal_award(token, selectedFeedbackUser.usr_id, feedbackComment.trim());
        setFeedbackSending(false);
        if (result && result.success) {
            closeFeedbackModal();
        } else {
            alert(result?.error || 'Error al enviar el feedback');
        }
    };

    const openUserStatsModal = (e, user) => {
        e.stopPropagation();
        setSelectedUserStats({
            usrId: Number(user.usr_id),
            usrName: user.usr_name || user.email_id || 'Usuario',
            emailId: String(user.email_id || '').trim(),
            title: `Progreso de ${user.usr_name || user.email_id || 'usuario'}`
        });
        setShowUserStatsModal(true);
    };

    if (loading) {
        return (
            <div className="bg-white pt-0 pb-2 px-2 rounded-3 shadow-sm w-100">
                <div className="text-muted">Cargando...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white pt-0 pb-2 px-2 rounded-3 shadow-sm w-100">
                <div className="text-danger">{error}</div>
            </div>
        );
    }

    return (
        <div
            className="bg-white pt-0 pb-2 px-2 rounded-3 shadow-sm w-100"
            onClick={() => {
                if (showStatsModal) return;
                setSelectedUserId(null);
                setSelectedStrategyKey(null);
            }}
        >
            <style>{`
                .activity-map-canvas {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    background: #fcfdff;
                    overflow: auto;
                    height: calc(100vh - 170px);
                    max-height: calc(100vh - 170px);
                }
                .strategy-kr-zone {
                    position: absolute;
                    border: 2px solid #4b5563;
                    border-radius: 2px;
                    background: #fff;
                    pointer-events: none;
                    z-index: 0;
                }
                .strategy-kr-zone-title {
                    position: absolute;
                    top: 6px;
                    left: 10px;
                    font-size: 0.95rem;
                    font-weight: 500;
                    color: #9ca3af;
                }
                .strategy-map-card {
                    background: #fff;
                    border: 1px solid #d1d5db;
                    border-radius: 10px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
                    padding: 0.35rem 0.45rem;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    text-align: center;
                }
                .strategy-map-card.is-opportunity {
                    background: #eaf4ff;
                    border-color: #93c5fd;
                }
                .strategy-map-title {
                    font-size: 0.84rem;
                    font-weight: 600;
                    color: #111827;
                    line-height: 1.1;
                    white-space: normal;
                    overflow-wrap: anywhere;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-height: calc(1.1em * 2);
                    max-height: calc(1.1em * 2);
                }
                .strategy-map-meta {
                    display: none;
                }
                .map-user-node {
                    position: absolute;
                    transform: translate(-50%, -50%);
                    width: ${AVATAR_SIZE}px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.2rem;
                    z-index: 40;
                }
                .map-user-avatar {
                    border-radius: 999px;
                    padding: 2px;
                    background: transparent;
                    position: relative;
                    z-index: 42;
                }
                .map-user-actions {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 150px;
                    transform: translate(-50%, -50%);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    z-index: 43;
                    pointer-events: none;
                }
                .map-user-action-btn {
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    width: 28px;
                    height: 28px;
                    border-radius: 999px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    pointer-events: auto;
                }
                .map-user-action-btn:hover {
                    color: #0d6efd;
                    background: rgba(13, 110, 253, 0.1);
                }
                .map-user-avatar.is-active {
                    box-shadow: 0 0 0 2px #16a34a;
                }
                .map-user-avatar.is-inactive {
                    filter: grayscale(1) saturate(0);
                    box-shadow: 0 0 0 1px #9ca3af;
                    background: #f3f4f6;
                }
                .map-user-name {
                    display: none;
                }
                .map-user-badges {
                    position: absolute;
                    bottom: calc(100% + 6px);
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    gap: 4px;
                    flex-wrap: wrap;
                    justify-content: center;
                    min-width: 110px;
                    z-index: 44;
                }
                .map-user-badge {
                    font-size: 0.62rem;
                    line-height: 1.1;
                    padding: 2px 6px;
                    border-radius: 8px;
                    border: 1px solid #d1d5db;
                    background: #ffffff;
                    color: #4b5563;
                    min-width: 170px;
                    white-space: normal;
                    max-width: 340px;
                    word-break: break-word;
                }
                .map-user-badge.active {
                    border-color: #16a34a;
                    color: #166534;
                    background: #ecfdf5;
                }
                .users-outside-canvas {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                }
                .map-top-controls .form-select {
                    font-size: 0.8rem;
                    min-height: 30px;
                    padding-top: 0.1rem;
                    padding-bottom: 0.1rem;
                }
                .map-top-controls .btn {
                    min-height: 30px;
                    padding: 0.15rem 0.45rem;
                }
                .users-status-groups {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                }
                .users-status-group {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .users-status-label {
                    font-size: 0.72rem;
                    color: #9ca3af;
                    font-weight: 600;
                }
                .outside-user-chip {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1px;
                    border-radius: 999px;
                    border: 1px dashed #9ca3af;
                    background: #f3f4f6;
                    filter: grayscale(1) saturate(0);
                    transform: scale(0.84);
                    transform-origin: center;
                }
                .outside-user-chip.inactive-now {
                    border-style: solid;
                    border-color: #cbd5e1;
                    background: #eef2f7;
                }
            `}</style>

            <div className="map-top-controls d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Periodo:</span>
                    <select
                        className="form-select form-select-sm"
                        value={periodDays}
                        style={{ width: 'auto' }}
                        onChange={(e) => setPeriodDays(Number(e.target.value))}
                    >
                        {PERIOD_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                        title="Actualizar mapa"
                        aria-label="Actualizar mapa"
                        onClick={(e) => {
                            e.stopPropagation();
                            setRefreshNonce((prev) => prev + 1);
                        }}
                        disabled={loading || isRefreshing}
                    >
                        <i className={`bi ${isRefreshing ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} />
                    </button>
                </div>
                {(inactiveNowUsers.length > 0 || usersWithoutPeriodTasks.length > 0) && (
                    <div className="users-outside-canvas" onClick={(e) => e.stopPropagation()}>
                        <div className="users-status-groups">
                            {inactiveNowUsers.length > 0 && (
                                <div className="users-status-group">
                                    {inactiveNowUsers.map((user) => (
                                        <div
                                            key={`inactive-now-${user.usr_id}`}
                                            className="outside-user-chip inactive-now"
                                            title={`${user.usr_name || user.email_id || 'Usuario'} (sin tarea activa en este momento)`}
                                        >
                                            <UserAvatar usr_id={Number(user.usr_id)} usr_name={user.usr_name} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {usersWithoutPeriodTasks.length > 0 && (
                                <div className="users-status-group">
                                    <span className="users-status-label">Sin tareas del periodo:</span>
                                    {usersWithoutPeriodTasks.map((user) => (
                                        <div
                                            key={`outside-${user.usr_id}`}
                                            className="outside-user-chip"
                                            title={`${user.usr_name || user.email_id || 'Usuario'} (sin tareas en este periodo)`}
                                        >
                                            <UserAvatar usr_id={Number(user.usr_id)} usr_name={user.usr_name} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div>
            <div
                className="activity-map-canvas"
                ref={canvasViewportRef}
                onClick={() => {
                    setSelectedUserId(null);
                    setSelectedStrategyKey(null);
                }}
            >
                <div style={{ width: canvasWidth, height: canvasHeight, position: 'relative' }}>
                    {[krZones.ventas, krZones.producto, krZones.operaciones].map((zone) => (
                        <div
                            key={`zone-${zone.title}`}
                            className="strategy-kr-zone"
                            style={{
                                left: zone.x,
                                top: zone.y,
                                width: zone.w,
                                height: zone.h
                            }}
                        >
                            <div className="strategy-kr-zone-title">{zone.title}</div>
                        </div>
                    ))}
                    <svg
                        width={canvasWidth}
                        height={canvasHeight}
                        style={{ position: 'absolute', left: 0, top: 0, zIndex: 1 }}
                    >
                        {visibleCanvasUsers.map((user) => {
                            const uid = Number(user.usr_id);
                            const source = userPositions[uid];
                            if (!source) return null;
                            const links = userLinksMap[uid] || [];
                            return links.map((link, index) => {
                                const target = strategyPositions[link.strategy_key];
                                if (!target || !displayedStrategyKeySet.has(link.strategy_key)) return null;
                                return (
                                    <line
                                        key={`${uid}-${link.strategy_key}-${index}`}
                                        x1={source.x}
                                        y1={source.y}
                                        x2={target.cx}
                                        y2={target.cy}
                                        stroke={link.has_active_task ? '#16a34a' : '#9ca3af'}
                                        strokeWidth={link.has_active_task ? 2.2 : 1.7}
                                        strokeDasharray={link.has_active_task ? '0' : '6 5'}
                                        opacity={0.85}
                                    />
                                );
                            });
                        })}
                    </svg>

                    {displayedStrategies.map((strategy) => {
                        const pos = strategyPositions[strategy.strategy_key];
                        if (!pos) return null;
                        return (
                            <div
                                key={strategy.strategy_key}
                                className={`strategy-map-card ${strategy.is_opportunity ? 'is-opportunity' : ''}`}
                                style={{
                                    position: 'absolute',
                                    left: pos.x,
                                    top: pos.y,
                                    width: CARD_W,
                                    height: CARD_H,
                                    zIndex: 10,
                                    cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedStrategyKey((prev) => (prev === strategy.strategy_key ? null : strategy.strategy_key));
                                    setSelectedUserId(null);
                                }}
                            >
                                <div className="strategy-map-title" title={strategy.strategy_name}>
                                    {truncateTitle(strategy.strategy_name, 20)}
                                </div>
                            </div>
                        );
                    })}

                    {visibleCanvasUsers.map((user) => {
                        const uid = Number(user.usr_id);
                        const pos = userPositions[uid];
                        if (!pos) return null;
                        const isActive = Boolean(userTotalsMap[uid]?.has_any_active_task);
                        const userLinks = (userLinksMap[uid] || []).filter((link) => displayedStrategyKeySet.has(link.strategy_key));
                        const activeLinks = userLinks.filter((link) => link.has_active_task);
                        const badgeLinks = activeLinks.slice(0, 2);
                        const hiddenBadgeCount = activeLinks.length - badgeLinks.length;

                        return (
                            <div
                                key={`user-${uid}`}
                                className="map-user-node"
                                style={{
                                    left: pos.x,
                                    top: pos.y
                                }}
                                title={user.usr_name || user.email_id || 'Usuario'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUserId((prev) => (Number(prev) === uid ? null : uid));
                                    setSelectedStrategyKey(null);
                                }}
                            >
                                {isFocusMode && (
                                    <div className="map-user-actions">
                                        <button
                                            type="button"
                                            className="map-user-action-btn"
                                            title="Ver estadisticas de este usuario"
                                            aria-label="Ver estadisticas de este usuario"
                                            onClick={(e) => openUserStatsModal(e, user)}
                                        >
                                            <i className="bi bi-box-arrow-up-right" style={{ fontSize: '1.05rem' }} />
                                        </button>
                                        <button
                                            type="button"
                                            className="map-user-action-btn"
                                            title="Dar reconocimiento o feedback"
                                            aria-label="Dar reconocimiento o feedback"
                                            onClick={(e) => openFeedbackModal(e, user)}
                                        >
                                            <i className="bi bi-chat-left-quote" style={{ fontSize: '1.05rem' }} />
                                        </button>
                                    </div>
                                )}
                                <div className={`map-user-avatar ${isActive ? 'is-active' : 'is-inactive'}`}>
                                    <UserAvatar usr_id={uid} usr_name={user.usr_name} />
                                </div>
                                {isFocusMode && badgeLinks.length > 0 && (
                                    <div className="map-user-badges">
                                        {badgeLinks.map((link, idx) => {
                                            const strategy = strategyByKey[link.strategy_key];
                                            const badgeText = link.active_task_name || strategy?.strategy_name || 'Tarea activa';
                                            return (
                                                <span
                                                    key={`${uid}-badge-${idx}-${link.strategy_key}`}
                                                    className={`map-user-badge ${link.has_active_task ? 'active' : ''}`}
                                                    title={badgeText}
                                                    role={link.has_active_task ? 'button' : undefined}
                                                    tabIndex={link.has_active_task ? 0 : undefined}
                                                    onClick={(e) => {
                                                        if (!link.has_active_task) return;
                                                        e.stopPropagation();
                                                        openActiveTaskStats(uid, link, badgeText);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (!link.has_active_task) return;
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openActiveTaskStats(uid, link, badgeText);
                                                        }
                                                    }}
                                                >
                                                    {badgeText}
                                                </span>
                                            );
                                        })}
                                        {hiddenBadgeCount > 0 && (
                                            <span className="map-user-badge">+{hiddenBadgeCount}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            </div>

            <Modal
                show={showStatsModal && !!selectedTaskStats?.taskId}
                onHide={() => setShowStatsModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedTaskStats?.title || 'Detalle de tarjeta'}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {selectedTaskStats?.taskId ? (
                        <Statistics
                            usr_id={selectedTaskStats.usrId}
                            token={token}
                            cards={[]}
                            taskId={selectedTaskStats.taskId}
                            isEmbedded={true}
                            onCloseEmbedded={() => setShowStatsModal(false)}
                        />
                    ) : (
                        <div className="text-muted">No hay tarea disponible para mostrar detalle.</div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={showUserStatsModal && !!selectedUserStats?.usrId}
                onHide={() => setShowUserStatsModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedUserStats?.title || 'Progreso de usuario'}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {selectedUserStats?.usrId ? (
                        <Task_progress
                            token={token}
                            filter_complete={null}
                            title={selectedUserStats?.title}
                            self_cards={[]}
                            member_trello_id={null}
                            initialEmailId={selectedUserStats?.emailId || null}
                            isEmbedded={true}
                        />
                    ) : (
                        <div className="text-muted">No hay usuario disponible para mostrar detalle.</div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal_proyectos
                isOpen={showFeedbackModal}
                onClose={closeFeedbackModal}
                title="Reconocimiento y/o feedback directo y sincero"
            >
                {selectedFeedbackUser && (
                    <div className="d-flex flex-column gap-3 p-4">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted">Para:</span>
                            <UserAvatar usr_id={selectedFeedbackUser.usr_id} usr_name={selectedFeedbackUser.usr_name} />
                            <span className="fw-semibold">{selectedFeedbackUser.usr_name || 'Usuario'}</span>
                        </div>
                        <div>
                            <label htmlFor="map-feedback-textarea" className="form-label text-muted small mb-1">Tu mensaje</label>
                            <textarea
                                id="map-feedback-textarea"
                                className="form-control"
                                rows={4}
                                placeholder="Escribe aqui tu reconocimiento o feedback..."
                                value={feedbackComment}
                                onChange={(e) => setFeedbackComment(e.target.value)}
                                disabled={feedbackSending}
                            />
                        </div>
                        <div className="modal-footer-fixed d-flex justify-content-end gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={closeFeedbackModal}
                                disabled={feedbackSending}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSendFeedback}
                                disabled={!feedbackComment.trim() || feedbackSending}
                            >
                                {feedbackSending ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal_proyectos>
        </div>
    );
}

export default Current_activity_map;
